import React from 'react';
import { Target, TrendingUp, TrendingDown, XCircle } from 'lucide-react';

/**
 * SuccessRateCard - Displays daily delivery success metrics.
 * No bonus tier shown here — bonuses are calculated and paid monthly.
 */
function SuccessRateCard({ successStats }) {
  if (!successStats) return null;

  const {
    total_assigned = 0,
    entregados = 0,
    rechazados = 0,
    cancelados = 0,
    success_rate = 0,
    rechazados_rate = 0,
    cancelados_rate = 0,
  } = successStats;

  if (total_assigned === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 col-span-2">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-gray-100 p-2 rounded-lg">
            <Target className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-xs font-semibold text-gray-500 font-montserrat">Tasa de Éxito del Día</p>
        </div>
        <p className="text-sm text-gray-400">Sin asignaciones finalizadas este día</p>
      </div>
    );
  }

  const getBarColor = (rate) => {
    if (rate >= 88) return 'bg-amber-500';
    if (rate >= 83) return 'bg-brand-green';
    if (rate >= 75) return 'bg-blue-500';
    return 'bg-red-400';
  };

  const getRateColor = (rate) => {
    if (rate >= 88) return 'text-amber-600';
    if (rate >= 83) return 'text-brand-green';
    if (rate >= 75) return 'text-blue-600';
    return 'text-red-500';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 col-span-2">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="bg-brand-primary/10 p-2 rounded-lg">
          <Target className="w-5 h-5 text-brand-primary" />
        </div>
        <p className="text-xs font-semibold text-gray-700 font-montserrat">Tasa de Éxito del Día</p>
      </div>

      {/* Big percentage */}
      <div className="flex items-end gap-2 mb-2">
        <p className={`text-4xl font-bold font-montserrat leading-none ${getRateColor(success_rate)}`}>
          {success_rate.toFixed(1)}
          <span className="text-xl font-semibold text-gray-400">%</span>
        </p>
        <p className="text-xs text-gray-500 mb-1 pb-0.5">
          {entregados}/{total_assigned} entregas
        </p>
      </div>

      {/* Progress bar — no threshold markers, just a clean bar */}
      <div className="w-full bg-gray-100 rounded-full h-2 mb-4 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${getBarColor(success_rate)}`}
          style={{ width: `${Math.min(success_rate, 100)}%` }}
        />
      </div>

      {/* Breakdown row */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-green-50 rounded-lg p-2 text-center">
          <TrendingUp className="w-3 h-3 text-green-600 mx-auto mb-1" />
          <p className="text-base font-bold text-green-700">{entregados}</p>
          <p className="text-[10px] text-green-600 font-montserrat">Entregados</p>
        </div>

        <div className="bg-red-50 rounded-lg p-2 text-center">
          <TrendingDown className="w-3 h-3 text-red-500 mx-auto mb-1" />
          <p className="text-base font-bold text-red-600">{rechazados}</p>
          <p className="text-[10px] text-red-500 font-montserrat">Rechazados</p>
          {rechazados_rate > 0 && (
            <p className="text-[10px] text-red-400">{rechazados_rate.toFixed(1)}%</p>
          )}
        </div>

        <div className="bg-orange-50 rounded-lg p-2 text-center">
          <XCircle className="w-3 h-3 text-orange-500 mx-auto mb-1" />
          <p className="text-base font-bold text-orange-600">{cancelados}</p>
          <p className="text-[10px] text-orange-500 font-montserrat">Cancelados</p>
          {cancelados_rate > 0 && (
            <p className="text-[10px] text-orange-400">{cancelados_rate.toFixed(1)}%</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default SuccessRateCard;